import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { Lesson, Organisation } from '../api/types'
import { Face } from '../components/primitives'
import { radius } from '../components/primitives/shape'
import { Overlay } from '../components/Overlay'

/**
 * How many rules the always-on core holds, from `models.MAX_ALWAYS_LESSONS`.
 *
 * Copied rather than fetched, because it is a sentence in a heading and not a
 * rule anything here enforces; the server refuses the twenty-first and this
 * pane shows what it said. If it moves, this is the line that goes stale, which
 * is why the number is here once instead of inside a template string.
 */
const ALWAYS_CORE_CAP = 20

/**
 * The sheet, from the repository rail.
 *
 * A wrapper and nothing else: the same body serves the Settings pane, because
 * compact has no rail and its bottom bar has four slots already spoken for.
 */
export function Lessons({
  organisation,
  compact,
  onClose,
}: {
  organisation: Organisation
  compact: boolean
  onClose: () => void
}) {
  return (
    <Overlay title="Lessons" subtitle={organisation.name} compact={compact} onClose={onClose}>
      <LessonsPane organisation={organisation} />
    </Overlay>
  )
}

/**
 * What the organisation has learned, for a person to read.
 *
 * The server has had the whole corpus behind an API since lessons shipped, and
 * until now there was no way to see any of it without being an agent. Somebody
 * corrected an agent months ago, the agent wrote the rule down, and the only
 * evidence either happened was that agents stopped making the mistake. That is
 * a strange thing to ask a person to take on trust.
 *
 * Built around the split that is the actual design, and flattening it into one
 * sorted list would be wrong:
 *
 *   - **The core** is injected into every session before it does anything,
 *     whatever the task. Small on purpose: `MAX_ALWAYS_LESSONS` is the entire
 *     budget, and a corpus that tried to inject everything would start dropping
 *     rules to fit, which is the failure the file-based system had.
 *   - **Everything else** is retrieved when an agent says what it is about to
 *     do. Which is why the search box asks for a *task* rather than a keyword:
 *     asking it the way an agent asks it is the only way to see what an agent
 *     would actually be handed.
 *
 * One owner-gated action, and the line is the one already written down for
 * org-scoped settings, which is reversibility rather than blast radius. Writing
 * a lesson is undone by writing another; promoting one changes what every
 * session in the organisation reads before its first action.
 */
export function LessonsPane({ organisation }: { organisation: Organisation }) {
  const [all, setAll] = useState<Lesson[] | null>(null)
  const [core, setCore] = useState<Lesson[]>([])
  const [failed, setFailed] = useState(false)
  const [task, setTask] = useState('')
  const [found, setFound] = useState<Lesson[] | null>(null)
  const [searching, setSearching] = useState(false)
  const [refused, setRefused] = useState('')

  const owns = organisation.role === 'owner'

  useEffect(() => {
    let live = true
    setFailed(false)
    Promise.all([api.lessons(organisation.slug), api.coreLessons(organisation.slug)])
      .then(([everything, always]) => {
        if (!live) return
        setAll(everything)
        setCore(always)
      })
      .catch(() => live && setFailed(true))
    return () => {
      live = false
    }
  }, [organisation.slug])

  async function search() {
    const described = task.trim()
    if (!described) {
      setFound(null)
      return
    }
    setSearching(true)
    try {
      setFound(await api.searchLessons(organisation.slug, described))
    } catch {
      setFound([])
    } finally {
      setSearching(false)
    }
  }

  /**
   * Reload both lists rather than moving a row between them.
   *
   * The core has a cap the server enforces, so a toggle that succeeded locally
   * and was refused at the cap would leave the pane showing a rule as always-on
   * that no session will ever be handed.
   */
  async function toggleAlways(lesson: Lesson) {
    setRefused('')
    try {
      await api.setLessonAlways(organisation.slug, lesson.id, !lesson.always)
    } catch (error) {
      // The cap is the likely one and it is a real answer, not a fault: the
      // core is full and something has to come out first. Swallowing it would
      // leave a button that does nothing at exactly the moment it matters.
      setRefused(error instanceof Error ? error.message : 'That could not be changed.')
      return
    }
    const [everything, always] = await Promise.all([
      api.lessons(organisation.slug),
      api.coreLessons(organisation.slug),
    ])
    setAll(everything)
    setCore(always)
  }

  /* The core is listed on its own above, so listing it again below is one rule
     twice with no way to tell the copies apart. */
  const coreIds = new Set(core.map((lesson) => lesson.id))
  const rest = (all ?? []).filter((lesson) => !coreIds.has(lesson.id))

  return (
    <>
      {/* A task, not a keyword, and the placeholder has to say so. Retrieval is
          what an agent gets when it says what it is about to do, and a person
          searching this the way they would search a wiki sees the worst of it:
          a lesson is written in the words of what went wrong and a task in the
          words of the job, and the two rarely share any. */}
      <form
        onSubmit={(event) => {
          event.preventDefault()
          void search()
        }}
        style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 24 }}
      >
        <input
          value={task}
          onChange={(event) => setTask(event.target.value)}
          aria-label="What are you about to do?"
          placeholder="What are you about to do? Try: renaming a database column"
          style={{
            flex: 1,
            minWidth: 0,
            padding: '9px 13px',
            borderRadius: radius.sm,
            background: 'var(--panel-strong)',
            border: '1px solid var(--border-strong)',
            fontSize: 13,
            color: 'var(--text)',
          }}
        />
        {found !== null && (
          <button
            type="button"
            onClick={() => {
              setFound(null)
              setTask('')
            }}
            style={{ fontSize: 12.5, color: 'var(--text-dim)', cursor: 'pointer', flex: 'none', padding: '0 6px' }}
          >
            Clear
          </button>
        )}
      </form>
      {refused && (
        <p
          role="alert"
          style={{
            margin: '0 0 18px',
            padding: '11px 14px',
            borderRadius: radius.sm,
            background: 'var(--panel-strong)',
            fontSize: 13,
            lineHeight: 1.55,
            color: 'var(--text-soft)',
          }}
        >
          {refused}
        </p>
      )}

      {failed && (
        <p role="alert" style={{ margin: 0, fontSize: 13.5, color: 'var(--text-dim)' }}>
          The lessons could not be loaded. Your agents are unaffected; this is the pane, not the corpus.
        </p>
      )}

      {!failed && all === null && (
        <p role="status" style={{ margin: 0, fontSize: 13.5, color: 'var(--text-dim)' }}>
          Reading what this organisation has learned…
        </p>
      )}

      {!failed && all !== null && found === null && (
        <>
          <Group
            title="Always on"
            note={`Injected into every session before it does anything · ${core.length} of ${ALWAYS_CORE_CAP}`}
          >
            {core.length === 0 ? (
              <Empty>
                Nothing is in the core yet. These are the rules that apply whatever the task, and an owner
                promotes one from below.
              </Empty>
            ) : (
              core.map((lesson) => (
                <Row
                  key={lesson.id}
                  lesson={lesson}
                  organisation={organisation.slug}
                  owns={owns}
                  onToggle={() => void toggleAlways(lesson)}
                />
              ))
            )}
          </Group>

          <Group
            title="Retrieved when it applies"
            note={`Handed to an agent that says what it is about to do · ${rest.length} ${rest.length === 1 ? 'rule' : 'rules'}`}
          >
            {all.length === 0 ? (
              <Empty>
                Nothing yet. A lesson gets written when you correct an agent and it records what it would not
                have worked out for itself, with <Command>stagepost learn</Command>. Nobody writes these by
                hand, which is the point of them.
              </Empty>
            ) : rest.length === 0 ? (
              <Empty>Every rule this organisation has is in the core above.</Empty>
            ) : (
              rest.map((lesson) => (
                <Row
                  key={lesson.id}
                  lesson={lesson}
                  organisation={organisation.slug}
                  owns={owns}
                  onToggle={() => void toggleAlways(lesson)}
                />
              ))
            )}
          </Group>
        </>
      )}

      {found !== null && (
        <Group
          title="Nearest this task"
          note={
            searching
              ? 'Asking…'
              : `What an agent describing that would be handed · ${found.length} ${found.length === 1 ? 'rule' : 'rules'}`
          }
        >
          {!searching && found.length === 0 && (
            <Empty>
              Nothing came back. Either no rule is near enough, or this server has no embedding key and
              retrieval is turned off. Neither loses a lesson: the corpus is still above.
            </Empty>
          )}
          {found.map((lesson) => (
            <Row
              key={lesson.id}
              lesson={lesson}
              organisation={organisation.slug}
              owns={owns}
              onToggle={() => void toggleAlways(lesson)}
            />
          ))}
        </Group>
      )}
    </>
  )
}

function Group({ title, note, children }: { title: string; note: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 30 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <h3
          style={{
            margin: 0,
            font: '600 10.5px var(--font-machine)',
            letterSpacing: '.16em',
            textTransform: 'uppercase',
            color: 'var(--text-dim)',
          }}
        >
          {title}
        </h3>
        <span style={{ fontSize: 12, color: 'var(--text-faint)' }}>{note}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{children}</div>
    </section>
  )
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: '16px 18px',
        borderRadius: radius.md,
        border: '1px dashed var(--border-strong)',
        fontSize: 13,
        lineHeight: 1.6,
        color: 'var(--text-dim)',
        textWrap: 'pretty',
      }}
    >
      {children}
    </div>
  )
}

function Command({ children }: { children: string }) {
  return (
    <code style={{ font: '400 12.5px var(--font-machine)', color: 'var(--text-body)' }}>{children}</code>
  )
}

/**
 * One rule, closed to its sentence and opened to the account behind it.
 *
 * Closed by default, and that follows from how they are written: the rule is
 * one imperative sentence under 400 characters because that is what gets
 * injected, while `--story` takes the whole account and is never injected
 * anywhere. A pane that showed both at once would bury twenty rules under
 * twenty essays, and the rules are the part anybody is here to read.
 */
function Row({
  lesson,
  organisation,
  owns,
  onToggle,
}: {
  lesson: Lesson
  organisation: string
  owns: boolean
  onToggle: () => void
}) {
  const [open, setOpen] = useState(false)
  /* `null` until somebody opens the list, so a pane full of superseding rules
     costs no requests until one is asked about. */
  const [earlier, setEarlier] = useState<Lesson[] | 'reading' | 'unreadable' | null>(null)
  const [showEarlier, setShowEarlier] = useState(false)
  const hasDetail = Boolean(lesson.story) || Boolean(lesson.repository_key)

  /**
   * Follow `supersedes_id` back one GET at a time, newest first.
   *
   * A version that cannot be read ends the chain there and keeps what came
   * before it: one missing row should not hide the ones that loaded. The seen
   * set is for a loop, which an update cannot make today but which would
   * otherwise fetch forever.
   */
  async function readEarlier(first: string) {
    setEarlier('reading')
    const versions: Lesson[] = []
    const seen = new Set([lesson.id])
    let next: string | null = first
    while (next && !seen.has(next)) {
      seen.add(next)
      let version: Lesson
      try {
        version = await api.lesson(organisation, next)
      } catch {
        break
      }
      versions.push(version)
      next = version.supersedes_id
    }
    setEarlier(versions.length > 0 ? versions : 'unreadable')
  }

  return (
    <div
      style={{
        borderRadius: radius.md,
        border: `1px solid ${lesson.always ? 'var(--accent-edge)' : 'var(--border)'}`,
        background: lesson.always ? 'rgba(110,168,254,.05)' : 'var(--panel)',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', gap: 12, padding: '14px 16px', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, lineHeight: 1.6, color: 'var(--text)', textWrap: 'pretty' }}>
            {lesson.rule}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
              marginTop: 9,
              font: '400 10.5px var(--font-machine)',
              color: 'var(--text-faint)',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Face name={lesson.author} size={16} ring="transparent" />
              {lesson.author}
            </span>
            <span>{new Date(lesson.learned_at).toLocaleDateString()}</span>
            {lesson.category && <span>{lesson.category}</span>}
            {/* Reinforcement is the evidence behind which rules deserve the
                core: an agent hit this again and found it already adequate. */}
            {lesson.reinforced_count > 0 && (
              <span style={{ color: 'var(--text-meta)' }}>
                held up {lesson.reinforced_count}×
              </span>
            )}
            {hasDetail && (
              <button
                type="button"
                onClick={() => setOpen(!open)}
                aria-expanded={open}
                style={{ font: 'inherit', color: 'var(--accent)', cursor: 'pointer' }}
              >
                {open ? 'less' : 'what happened'}
              </button>
            )}
            {lesson.supersedes_id && (
              <button
                type="button"
                onClick={() => {
                  if (!showEarlier && earlier === null && lesson.supersedes_id) {
                    void readEarlier(lesson.supersedes_id)
                  }
                  setShowEarlier(!showEarlier)
                }}
                aria-expanded={showEarlier}
                style={{ font: 'inherit', color: 'var(--accent)', cursor: 'pointer' }}
              >
                earlier versions
              </button>
            )}
          </div>
        </div>

        {owns && (
          <button
            type="button"
            onClick={onToggle}
            className="sp-tap"
            style={{
              flex: 'none',
              padding: '5px 10px',
              borderRadius: radius.xs,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              font: '500 10px var(--font-machine)',
              letterSpacing: '.1em',
              textTransform: 'uppercase',
              background: lesson.always ? 'var(--accent-wash)' : 'var(--panel-strong)',
              color: lesson.always ? 'var(--accent-bright)' : 'var(--text-meta)',
            }}
          >
            {lesson.always ? 'always on' : 'make always on'}
          </button>
        )}
      </div>

      {open && (
        <div
          style={{
            padding: '13px 16px',
            borderTop: '1px solid var(--border)',
            background: 'rgba(0,0,0,.25)',
          }}
        >
          {lesson.story && (
            <div
              style={{
                fontSize: 13,
                lineHeight: 1.65,
                color: 'var(--text-dim)',
                whiteSpace: 'pre-wrap',
                textWrap: 'pretty',
              }}
            >
              {lesson.story}
            </div>
          )}
          {lesson.repository_key && (
            <div style={{ marginTop: lesson.story ? 12 : 0, font: '400 10.5px var(--font-machine)', color: 'var(--text-faint)' }}>
              learned on {lesson.repository_key}, and it applies everywhere
            </div>
          )}
        </div>
      )}

      {/* Superseded, never deleted: the rules this one sharpened, newest first. */}
      {showEarlier && (earlier === 'reading' || earlier === 'unreadable') && (
        <div
          role={earlier === 'reading' ? 'status' : 'alert'}
          style={{
            padding: '13px 16px',
            borderTop: '1px solid var(--border)',
            background: 'rgba(0,0,0,.25)',
            font: '400 10.5px var(--font-machine)',
            color: 'var(--text-faint)',
          }}
        >
          {earlier === 'reading' ? 'reading earlier versions…' : 'earlier versions could not be read'}
        </div>
      )}
      {showEarlier && Array.isArray(earlier) && (
        <ol
          aria-label="Earlier versions"
          style={{
            margin: 0,
            padding: '13px 16px',
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            borderTop: '1px solid var(--border)',
            background: 'rgba(0,0,0,.25)',
          }}
        >
          {earlier.map((version) => (
            <li key={version.id}>
              <div style={{ font: '400 10.5px var(--font-machine)', color: 'var(--text-faint)' }}>
                {new Date(version.learned_at).toLocaleDateString()}
              </div>
              <div style={{ marginTop: 4, fontSize: 13, lineHeight: 1.6, color: 'var(--text-dim)', textWrap: 'pretty' }}>
                {version.rule}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}
